import { useCallback, useEffect, useRef, useState } from 'react'

import { Box, Card, Button as DeleteButton, Text } from '@factorialco/deprecated-design-system'
import { F0Alert, F0TagRaw } from '@factorialco/f0-react'

import i18n from 'modules/core/lib/i18n'

import RatesSingleForm from 'modules/project_management/components/ProfitabilityCards/RatesSingleForm'
import { getPeopleCount } from 'modules/project_management/lib/constants'
import { DEFAULT_PROFIT_MARGIN } from 'modules/project_management/lib/constants'
import {
  calculateBillRateFromProfitability,
  calculateProfitabilityPercentage,
} from 'modules/project_management/lib/profitabilityCalculations'

interface IRateConfigurationCard {
  currency: string
  disabledAllFields?: boolean
  initialBillRate: string
  maxCostRate: number
  profitMargin: number
  onChangeValue: (values: { billRate: string; minProfitMargin: string }) => void
  onDelete?: () => void
  peopleCount?: number
  title: string
  sameProfitMargin?: boolean
}

const RateConfigurationCard = ({
  currency,
  disabledAllFields = false,
  initialBillRate,
  maxCostRate,
  profitMargin,
  onChangeValue,
  onDelete,
  peopleCount,
  title,
  sameProfitMargin,
}: IRateConfigurationCard) => {
  const [minProfitMargin, setMinProfitMargin] = useState<string>(
    profitMargin?.toString() || DEFAULT_PROFIT_MARGIN
  )

  const [billRate, setBillRate] = useState<string>(initialBillRate)
  const [errors, setErrors] = useState<{ billRate?: string; minProfitMargin?: string }>({})
  const userHasEdited = useRef(false)
  const onChangeValueRef = useRef(onChangeValue)

  useEffect(() => {
    onChangeValueRef.current = onChangeValue
  }, [onChangeValue])

  const calculateProfitMargin = (maxCostRateValue: number, billRateValue: string): string => {
    const bill = parseFloat(billRateValue) || 0
    return calculateProfitabilityPercentage(maxCostRateValue, bill)
  }

  const calculateBillRate = (maxCostRateValue: number, profitMarginValue: string): string => {
    const parsedProfitMargin = parseFloat(profitMarginValue) || 0
    return calculateBillRateFromProfitability(maxCostRateValue, parsedProfitMargin)
  }

  const handleBillRateChange = useCallback(
    (value: string) => {
      userHasEdited.current = true

      setBillRate(value)

      if (+value <= 0 || value === '') {
        setErrors((prev) => ({ ...prev, billRate: i18n.t('common.invalid_value') }))
      } else {
        setErrors((prev) => ({ ...prev, billRate: undefined }))
      }

      const newProfitMargin = calculateProfitMargin(maxCostRate, value)
      setMinProfitMargin(newProfitMargin)
    },
    [maxCostRate]
  )

  const handleProfitMarginChange = useCallback(
    (value: string) => {
      userHasEdited.current = true

      setMinProfitMargin(value)

      const newBillRate = calculateBillRate(maxCostRate, value)
      setBillRate(newBillRate)
    },
    [maxCostRate]
  )

  useEffect(() => {
    onChangeValueRef.current({
      billRate: billRate,
      minProfitMargin: minProfitMargin,
    })
  }, [billRate, minProfitMargin])

  // Sync with parent props when they change externally (only if user hasn't edited)
  useEffect(() => {
    // Only update if user hasn't edited
    if (!userHasEdited.current && maxCostRate > 0) {
      const calculatedBillRate = !sameProfitMargin
        ? billRate
        : calculateBillRate(maxCostRate, profitMargin.toString())
      setBillRate(calculatedBillRate)
      setMinProfitMargin(profitMargin.toString())
    }
  }, [profitMargin, maxCostRate, billRate, sameProfitMargin])

  useEffect(() => {
    if (initialBillRate) {
      setBillRate(initialBillRate)
    }
  }, [initialBillRate])

  const InfoBanner = () => {
    return (
      <Box paddingTop="s24">
        <F0Alert
          variant="info"
          title={i18n.t(
            'project_management.jobs_and_rates.sidepanels.alerts.no_people_in_this_job.title'
          )}
          description={i18n.t(
            'project_management.jobs_and_rates.sidepanels.alerts.no_people_in_this_job.description'
          )}
        />
      </Box>
    )
  }

  return (
    <Card>
      <Card.Content>
        <Box width="full" flexDirection="row" flexGrow>
          <Box
            inline
            flexDirection="column"
            justifyContent="flexStart"
            width="full"
            paddingBottom="s6"
          >
            <Box flexDirection="row" gap="s8" paddingBottom="s24">
              <Text size="300" color="grey1000" weight="semibold" ellipsis>
                {title}
              </Text>
              {peopleCount && <F0TagRaw text={getPeopleCount(peopleCount)} />}
            </Box>
            <RatesSingleForm
              billRate={billRate}
              currency={currency}
              disabledAllFields={disabledAllFields}
              errors={errors}
              maxCostRate={maxCostRate.toString()}
              minProfitMargin={minProfitMargin}
              handleBillRateChange={handleBillRateChange}
              handleProfitMarginChange={handleProfitMarginChange}
              areJobLevelRates
            />
            {peopleCount === 0 && <InfoBanner />}
            <Box paddingBottom="s24" />
            {onDelete && (
              <DeleteButton
                onClick={onDelete}
                iconName="delete"
                aria-label={i18n.t('common.delete')}
              />
            )}
          </Box>
        </Box>
      </Card.Content>
    </Card>
  )
}

export default RateConfigurationCard
